/* eslint-disable react-hooks/exhaustive-deps */
import { Popper, TextField } from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import React, { forwardRef, useMemo } from "react";
import { FastField, getIn } from "formik";
import ErrorIcon from "./ErrorIcon";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";

const AutoWidthPopper = (props) => {
    const { style: { width } } = props;
    const style = { minWidth: 'fit-content', width }
    return <Popper {...props} style={style} />;
}

const GlobitsAutocomplete = (props, ref) => (
    <FastField
        {...props}
        name={props.name}
        shouldUpdate={shouldComponentUpdate}
    >
        {({ field, meta, form }) => (
            <Component
                {...props}
                field={field}
                meta={meta}
                setFieldValue={form.setFieldValue}
                ref={ref}
            />
        )}
    </FastField>
);

const Component = forwardRef((props, ref) => {
    const {
        disabled,
        fullWidth = configDefaultForm.fullWidth,
        label,
        name,
        size = configDefaultForm.size,
        variant = configDefaultForm.variant,
        className = "",
        required,
        requiredLabel,
        onChange,
        readOnly,
        value,
        displayLabel = 'name',
        displaySelected = 'id',
        options = [],
        field,
        meta,
        setFieldValue,
        multiple,
        getOptionLabel,
        defaultValue = multiple ? [] : null,
        getOptionDisabled,
        noOptionsText = "Không có dữ liệu",
        getOptionSelected,
        disableTyping = false,
        updateParent,
        placeholder,
        handleFilter,
        searchFields = [displayLabel],
        autoFocus,
        autoWidthPopper = false,
        disableClearable = false,
        colorText,
        tabIndex,
        handleOnClick,
        ...otherProps
    } = props;

    const getOptionLabelDefault = useMemo(() => getOptionLabel ? getOptionLabel : (option) => getIn(option, displayLabel) || "", [getOptionLabel])
    const getOptionSelectedDefault = useMemo(() => (
        getOptionSelected ? getOptionSelected : (option, value) => {
            if (!value) {
                return false;
            }
            return getIn(option, displaySelected) === getIn(value, displaySelected);
        }
    ), [getOptionSelected]);

    const handleChange = (event, value, ...params) => {
        if (!value) {
            value = null
        }

        if (onChange) {
            event.target.id = name;
            onChange({ ...event, id: name }, value, ...params);
        } else {
            setFieldValue(name, value);
        }

        if (updateParent) {
            updateParent(value);
        }
    };

    const isError = meta && meta.touched && meta.error;
    const disLabel = useMemo(() => label ? <RequiredLabel label={label} requiredLabel={required || requiredLabel} /> : null, [label, required, requiredLabel]);

    const filterOptions = createFilterOptions({
        stringify: (obj) => {
            let result = "";
            for (const nameSearch of searchFields) {
                const fields = nameSearch.split("."); // Chia chuỗi thành một mảng các trường
                let value = obj;

                for (const field of fields) {
                    value = value ? value[field] : undefined; // Truy cập lần lượt các trường con
                }

                result += value !== undefined ? `${value} ` : ""; // Thêm giá trị vào chuỗi kết quả, nếu không tồn tại thì thêm chuỗi rỗng
            }
            result = result.trim();

            return result;

        }
    });
    return (
        <>
            {(!fullWidth && disLabel) &&
                <label htmlFor={name}>
                    {disLabel}
                </label>
            }
            <Autocomplete
                {...field}
                {...otherProps}
                ref={ref}
                id={name}
                name={name}
                disabled={disabled || readOnly}
                fullWidth={fullWidth}
                label={(fullWidth && displayLabel) ? displayLabel : null}
                multiple={multiple}
                size={size}
                value={value || (field?.value ?? (multiple ? [] : null))}
                onChange={handleChange}
                options={options || []}
                getOptionLabel={getOptionLabelDefault}
                getOptionSelected={getOptionSelectedDefault}
                noOptionsText={noOptionsText}
                defaultValue={defaultValue}
                getOptionDisabled={getOptionDisabled}
                PopperComponent={autoWidthPopper ? AutoWidthPopper : undefined}
                disableClearable={disableClearable}

                autoHighlight
                openOnFocus
                filterOptions={filterOptions}
                // filterSelectedOptions
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                    return true;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={(fullWidth && disLabel) ? disLabel : null}
                        variant={variant}
                        placeholder={placeholder}
                        InputLabelProps={{
                            htmlFor: name,
                            shrink: true,
                        }}
                        onClick={handleOnClick && handleOnClick}
                        error={isError}
                        helperText={(fullWidth && isError) ? meta.error : ""}
                        inputProps={{
                            ...params.inputProps,
                            readOnly: readOnly || disableTyping,
                            style: {
                                color: colorText,
                            },
                            tabIndex: tabIndex
                        }}
                        className={`input-container ${className} ${readOnly ? 'read-only-autocomplete' : ''}`}
                        autoFocus={autoFocus || false}
                    />
                )}
            />
            {(isError && !fullWidth) && <ErrorIcon helperText={meta.error} />}
        </>
    )
})

const shouldComponentUpdate = (nextProps, currentProps) => (
    nextProps.name !== currentProps.name ||
    nextProps.value !== currentProps.value ||
    nextProps.handleChange !== currentProps.handleChange ||
    nextProps.onChange !== currentProps.onChange ||
    nextProps.label !== currentProps.label ||
    nextProps.required !== currentProps.required ||
    nextProps.disabled !== currentProps.disabled ||
    nextProps.readOnly !== currentProps.readOnly ||
    nextProps.options !== currentProps.options ||
    nextProps.disableTyping !== currentProps.disableTyping ||
    nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
    Object.keys(nextProps).length !== Object.keys(currentProps).length ||
    getIn(nextProps.formik.values, currentProps.name) !== getIn(currentProps.formik.values, currentProps.name) ||
    getIn(nextProps.formik.errors, currentProps.name) !== getIn(currentProps.formik.errors, currentProps.name) ||
    getIn(nextProps.formik.touched, currentProps.name) !== getIn(currentProps.formik.touched, currentProps.name)
);

export default React.memo(forwardRef(GlobitsAutocomplete));